import { twMerge } from 'tailwind-merge';
import { clsx, type ClassValue } from 'clsx';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date): string {
  const d = new Date(date);
  if (isNaN(d.getTime())) {
    return 'Invalid Date';
  }
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatRelativeTime(date: string | Date): string {
  const now = new Date();
  const target = new Date(date);
  const diffInSeconds = Math.floor((now.getTime() - target.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return `${diffInSeconds} seconds ago`;
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minutes ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hours ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} days ago`;
  }

  return formatDate(date);
}

// Compact, null-safe relative time suitable for table cells: "5m ago", "3h ago",
// "2d ago", "3mo ago", "1y ago". Returns "--" for null/undefined/invalid input
// so a missing value (e.g. mission never started) renders stably.
export function formatRelativeTimeCompact(value?: string | Date | null): string {
  if (value == null || value === '') return '--';
  const target = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(target.getTime())) return '--';
  const sec = Math.floor((Date.now() - target.getTime()) / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  const yr = Math.floor(day / 365);
  return `${yr}y ago`;
}

export function debounce<TArgs extends unknown[]>(
  func: (...args: TArgs) => unknown,
  wait: number
): (...args: TArgs) => void {
  let timeout: NodeJS.Timeout;
  return (...args: TArgs) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function generateId(): string {
  return Math.random().toString(36).substr(2, 9);
}

export function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export function formatFileSize(bytes: number): string {
  if (bytes < 0) return '0 Bytes';
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = bytes / Math.pow(k, i);

  // Show 1 decimal place for values >= 1KB
  const formatted = i === 0 ? value.toString() : value.toFixed(1);

  // Remove unnecessary trailing .0
  const cleanedValue = formatted.replace(/\.0$/, '');

  return cleanedValue + ' ' + sizes[i];
}

export function truncateText(text: string, maxLength: number, suffix = '...'): string {
  if (text.length === 0) return '';
  if (text.length <= maxLength) return text;

  // Truncate to maxLength characters, trim trailing spaces, and add suffix
  return text.slice(0, maxLength).trimEnd() + suffix;
}

export function formatPercentage(value: number, decimals = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

// ============= Case Conversion Utilities =============
// Backend uses snake_case, frontend uses camelCase

/**
 * Convert a snake_case string to camelCase
 */
export function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Convert a camelCase string to snake_case
 */
export function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

// Keys whose VALUES are Record<string, X> shapes where the inner keys are
// user data (stream names, column names) — NOT protocol field names. The
// converters preserve the inner keys as-is but still recurse into the values.
//
// Listed by their camelCase form because the check runs after key conversion
// on the read path and before key conversion on the write path; the camelCase
// form is what the application code sees in both directions.
//
// Added in WU-K4b: classifier-applier output (`suggested_config`) keyed by
// stream name, with nested `overrides` keyed by column name. Without these
// exemptions a stream named `order_items` becomes a map key `orderItems`,
// breaking lookup against the unchanged `cursor_field` value etc.
// Maps whose immediate keys are data, not field names: a stream name, an
// operation id, a customer's column. `selectionDigests` is a resource's
// per-stream selection digest map (ADR-20260922): camelizing
// `order_line_items` there would print a stream nobody approved and would no
// longer equal the stream the pin names. `sharepointStreams` is a capsule's
// metadata.sharepoint_streams lineage, keyed by stream name (agion/Agion#2297):
// "Read the library again" sends that name to the stream retry route, and
// `sharedDocuments` would name a stream the worker does not have.
// `capsuleNames` is the capsule-access read's capsule id to name map
// (agion/Agion#3131): the designer looks a name up by the id a principal
// names. `stepLabels` is the audit mission detail's step id to label map
// (agion/Agion#3432): the run view looks a label up by the step id an
// evidence key names, so `fetch_defects` must not reach it as `fetchDefects`.
const STRING_KEYED_MAP_KEYS = new Set([
  'capsuleNames',
  'stepLabels',
  'suggestedConfig',
  'overrides',
  'schemaOverrides',
  'modelConfigurations',
  'streams',
  'selectionDigests',
  'sharepointStreams',
]);

/**
 * Convert the immediate keys of `value` (a Record<string, X>) without
 * touching them, recursing into each value with the supplied converter.
 * Used for string-keyed maps where the key itself is user data.
 *
 * Returns `unknown` honestly: the caller assigns into a
 * `Record<string, unknown>` and a generic here would only be a lie.
 */
function recurseValuesPreserveKeys(
  value: unknown,
  recurse: (v: unknown) => unknown,
): unknown {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    // Not the expected map shape; fall back to ordinary recursion so we do
    // not silently pass through unexpected input unchanged.
    return recurse(value);
  }
  const result: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    result[k] = recurse(v);
  }
  return result;
}

/**
 * Recursively convert all keys in an object from snake_case to camelCase.
 * Values under keys listed in STRING_KEYED_MAP_KEYS keep their immediate
 * child keys verbatim (those keys are user data, not field names) but the
 * grandchildren are still converted normally.
 */
export function keysToCamel<T>(obj: unknown): T {
  if (Array.isArray(obj)) {
    return obj.map(item => keysToCamel(item)) as T;
  }

  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      const camelKey = snakeToCamel(key);
      if (OPAQUE_BLOB_KEYS.has(camelKey)) {
        result[camelKey] = value;
      } else if (STRING_KEYED_MAP_KEYS.has(camelKey)) {
        result[camelKey] = recurseValuesPreserveKeys(value, keysToCamel);
      } else {
        result[camelKey] = keysToCamel(value);
      }
    }
    return result as T;
  }

  return obj as T;
}

// Keys whose values are cross-service contracts (e.g., workflow DAG that flows
// admin-ui → orchestration-service → streaming-service → mission-runner) or
// JSON Schemas whose property keys are DATA, not wire field names:
// inputSchema.properties.original_pdf must stay snake_case end to end —
// mission-runner resolves host-file inputs and validates tool outputs by
// exact property name, so camelizing them breaks bindings at runtime
// ("output contract violation: declared output 'changeMetadata' not
// produced"). The key itself is still converted, but the value is preserved
// as-is, in BOTH directions (keysToCamel and keysToSnake).
const OPAQUE_BLOB_KEYS = new Set([
  'workflow',
  'workflowDefinition',
  'designDefinition',
  'sampleStats',
  'inputSchema',
  'outputSchema',
  // Provider field schemas are evidence, including exact custom-property names.
  'sourceSchema',
  // "How to run" contract (metadata.run_mode): consumed by mission-runner's
  // sweep parser by exact snake_case keys (worklist_stream, batch_size, ...).
  // Camelizing the value blinds the Run Mode UI section and a designer save
  // then round-trips runner-unreadable camelCase keys back into metadata.
  'runMode',
  // Bound-operation invocation snapshot (`resourceplan.InvocationSnapshot`,
  // wire version invocation_snapshot.v2). orchestration-service and
  // mission-runner read the envelope by exact snake_case keys (resource_id,
  // operation_id, transport, targets) and the whole object is sealed by
  // `digest`, so any key rewrite also invalidates the seal. Camelizing the
  // envelope makes the server's full-save gate reject every designer save with
  // "invocation snapshot resource_id is required"; it must travel verbatim in
  // both directions.
  'operationContract',
  // Declared source schemas of a parameter-plan validate request
  // (declared_sources, agion/Agion#2466). The inner keys are output refs
  // ("jointMatcher.linkJoints") and JSON Schema keywords and field names
  // (additionalProperties, minItems, docDescr) the planner reads verbatim.
  'declaredSources',
  // Webhook trigger contract on each trigger in a mission's
  // capabilities.triggers (contracttriggers.TriggerContract). Its fields are already the snake_case
  // wire shape the designer reads (input_bindings, mission_input,
  // request_schema), and it holds user data by name: a json constant
  // binding's own keys and the request schema's start value names (ObjectID).
  // Converting it renamed both on every classic designer save and load
  // (agion/Agion#1780). The trigger API (trigger.service.ts) sends it by
  // plain fetch and never reaches these converters.
  'triggerContract',
  // A REST resource's paging declaration (resource_data.rest_pagination,
  // agion/Agion#3543). governance-service and agion-mcp read it by exact
  // snake_case keys, and its operations map is keyed by operation id, so
  // `list_orders` must not come back as `listOrders`.
  'restPagination',
]);

/**
 * Recursively convert all keys in an object from camelCase to snake_case.
 * Values under keys listed in OPAQUE_BLOB_KEYS are preserved without recursion.
 * Values under keys listed in STRING_KEYED_MAP_KEYS keep their immediate
 * child keys verbatim (those keys are user data, not field names).
 */
export function keysToSnake<T>(obj: unknown): T {
  if (Array.isArray(obj)) {
    return obj.map(item => keysToSnake(item)) as T;
  }

  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      const snakeKey = camelToSnake(key);
      // Look up exemptions in their canonical camelCase form so a
      // hand-authored snake_case key (e.g. relayed wire data passed straight
      // to keysToSnake) still hits the exemption — symmetric with keysToCamel
      // which performs the lookup after snake→camel normalization.
      const camelKey = snakeToCamel(key);
      if (OPAQUE_BLOB_KEYS.has(camelKey)) {
        result[snakeKey] = value;
      } else if (STRING_KEYED_MAP_KEYS.has(camelKey)) {
        result[snakeKey] = recurseValuesPreserveKeys(value, keysToSnake);
      } else {
        result[snakeKey] = keysToSnake(value);
      }
    }
    return result as T;
  }

  return obj as T;
}
